<template>
  <div
    class="inline-block"
    :class="$style['vertical-box-align']"
  >
    <input
      type="checkbox"
      :name="name"
      :class="inputClasses"
      :value="value"
      :checked="state"
      v-bind="$attrs"
      v-on="listeners"
      @change="onChange"
    >

    <slot name="box">
      <span :class="boxClasses">
        <svg
          viewBox="0 0 16 16"
          class="w-full h-full"
          :class="$style['tick']"
        >
          <path
            fill="none"
            d="M1.7,7.8l3.8,3.4l9-8.8"
          />
        </svg>
      </span>
    </slot>

    <div
      v-if="iconName"
      class="absolute pin-t flex items-center h-full text-xl"
      :class="[$style['icon'], { 'text-primary-blue': valid, 'text-danger-50': invalid }]"
    >
      <VIcon :name="iconName" />
    </div>
  </div>
</template>

<script>
import _ from 'lodash'

import { getTypeOf } from '@/utils'

import CheckControl from '@c/base/mixins/CheckControl'
import {
  VIcon
} from '@c/base'

export default {
  mixins: [CheckControl],

  components: {
    VIcon
  },

  props: {
    boxClass: {
      type: Array,
      default: () => [
        'overflow-hidden',
        'm-0',
        'p-0',
        'bg-white',
        'border'
      ]
    },
    valid: {
      type: Boolean,
      default: false
    },
    invalid: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    state () {
      if (this.modelValue === undefined) {
        return this.checked
      }

      let type = this.modelValue === null ? getTypeOf(this.value) : getTypeOf(this.modelValue)

      switch (type) {
        case 'String':
          return this.modelValue === this.value

        case 'Number':
          return this.modelValue === this.value

        case 'Array':
          return this.modelValue.findIndex(modelVal => {
            return _.isEqual(modelVal, this.value)
          }) > -1

        case 'Object':
          let keys = Object.keys(this.value)
          let isChecked = false

          for (let i = 0; i < keys.length; i++) {
            isChecked = this.modelValue && this.modelValue[keys[i]]

            if (!isChecked) {
              break
            }
          }

          return isChecked

        default:
          return !!this.modelValue
      }
    },

    inputClasses () {
      return ['absolute', 'appearance-none', 'opacity-0', this.$style['box-size'], {
        'cursor-pointer': !this.disabled
      }]
    },

    boxClasses () {
      return [
        'flex',
        'items-center',
        'justify-center',
        this.$style['box'],
        this.$style['box-size'],
        ...this.boxClass,
        {
          [this.$style['checked']]: this.state,
          'border-grey-80': !this.state,
          'cursor-pointer': !this.disabled
        }
      ]
    },

    iconName () {
      if (this.invalid) {
        return 'exclamation-circle'
      }

      return this.valid
        ? 'check'
        : null
    }
  },

  watch: {
    state: function (newState) {
      this.$emit('change', { state: newState, modelValue: this.modelValue, value: this.value })
    }
  },

  methods: {
    toggle () {
      let type = this.modelValue === null
        ? getTypeOf(this.value)
        : getTypeOf(this.modelValue)
      let value

      switch (type) {
        case 'String':
          value = this.state ? '' : this.value

          break

        case 'Number':
          value = this.state ? null : this.value

          break

        case 'Array':
          value = this.modelValue.slice(0)

          if (this.state) {
            if (getTypeOf(this.value) === 'Object') {
              let index = _.findIndex(value, val => _.isEqual(val, this.value))

              value.splice(index, 1)
            } else {
              value.splice(value.indexOf(this.value), 1)
            }
          } else {
            value.push(this.value)
          }

          break

        case 'Object':
          if (this.state) {
            let keys = Object.keys(this.value)

            value = this.modelValue
            keys.forEach(key => {
              delete value[key]
            })
          } else {
            value = { ...this.modelValue, ...this.value }
          }

          break

        default:
          value = !this.state
      }

      this.$emit('input', value)
    }
  }
}
</script>

<style lang="scss" module>
.vertical-box-align {
  vertical-align: -0.2em;
}

.box-size {
  min-width: 1em;
  min-height: 1em;
  width: 1em;
  height: 1em;
}

.box {
  user-select: none;
  transition: background 0.1s ease;

  .tick {
    transform: scale(.9);
    margin-top: 0.1em;
    stroke: #00b0f0;

    path {
      opacity: 0;
      stroke-width: 2.3px;
      stroke-dashoffset: 20;
      stroke-dasharray: 20;
      transition: stroke-dashoffset 0.2s ease-in;
      will-change: stroke-dashoffset;
    }
  }

  &.checked > .tick > path {
    opacity: 1;
    stroke-dashoffset: 0;
  }
}

.icon {
  right: -30px;
}
</style>
